import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

/**
 * Knowledge Store index status API client (SCRUM-495, backend SCRUM-494).
 *
 * Contract: snapshot-then-subscribe. `fetchKnowledgeStatus` returns the
 * one-time map (documents ABSENT from the map are "not indexed" — the
 * backend represents that state by absence); live transitions then arrive
 * over Server-Sent Events from `knowledgeStatusStreamUrl()`.
 *
 * HARD RULE (2026-07-10): live updates are SSE — NEVER polling.
 *
 * The stream URL carries the tenant as a `tenantId` query parameter because
 * the browser's native EventSource cannot attach the App ID Bearer — the same
 * middleware fallback the Vault download navigation uses (TenantResolutionMiddleware
 * accepts the query param for token-less requests; authenticated fetch calls
 * derive the tenant from the token claim instead).
 *
 * SCRUM-643: the tenant is the AUTHENTICATED tenant passed in by the caller
 * (from the auth store's user.tenantId), not a hard-coded demo id. It remains a
 * client-supplied value on the EventSource path (no token can ride it); a
 * token-bearing SSE transport is a tracked follow-up.
 */
const STATUS_URL = '/api/intelligence/knowledge/status';

export const knowledgeIndexStates = ['indexing', 'indexed', 'failed', 'not_indexable'] as const;

const knowledgeStatusEntrySchema = z.object({
  status: z.enum(knowledgeIndexStates),
  detail: z.string().nullable(),
  chunksWritten: z.number(),
  updatedAt: z.string(),
});

export type KnowledgeStatusEntry = z.infer<typeof knowledgeStatusEntrySchema>;

/** The SSE event payload adds the document id to the entry shape. */
const knowledgeStatusEventSchema = knowledgeStatusEntrySchema.extend({
  documentId: z.string(),
});

export type KnowledgeStatusEvent = z.infer<typeof knowledgeStatusEventSchema>;

/** GET /api/intelligence/knowledge/status — documentId → status entry. */
export async function fetchKnowledgeStatus(): Promise<Record<string, KnowledgeStatusEntry>> {
  const res = await apiFetch(STATUS_URL);
  if (!res.ok) {
    throw new Error(`GET ${STATUS_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const raw: unknown = await res.json();
  return z.record(z.string(), knowledgeStatusEntrySchema).parse(raw);
}

/** The SSE stream URL (native EventSource — the authenticated tenant rides the query). */
export function knowledgeStatusStreamUrl(tenantId: string): string {
  return `${STATUS_URL}/stream?tenantId=${encodeURIComponent(tenantId)}`;
}

/** Parses one SSE `index-status` event's data payload; null when malformed. */
export function parseKnowledgeStatusEvent(data: string): KnowledgeStatusEvent | null {
  try {
    const parsed = knowledgeStatusEventSchema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
